import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Space, message } from "antd";
import {
  CheckCircleFilled,
  CopyOutlined,
  DownOutlined,
  RightOutlined,
} from "@ant-design/icons";
import {
  SETUP_STEP_KEYS,
  computeVoiceCaptureSetupSteps,
  setupProgress,
} from "./setupUtils";
import { captureCdnScriptUrl, getWidgetEmbedSnippet } from "./widgetHelpers";
import styles from "./style.module.scss";

const VoiceCaptureSetupChecklist = ({
  widget,
  apiReady,
  showProgressHeader = false,
  onActivate,
  onEnableEmbed,
  activating = false,
  enablingEmbed = false,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(() => new Set());

  const steps = useMemo(
    () => computeVoiceCaptureSetupSteps(widget, { apiReady }),
    [widget, apiReady],
  );
  const progress = setupProgress(steps);
  const embedSnippet = widget ? getWidgetEmbedSnippet(widget) : null;
  const cdnUrl = captureCdnScriptUrl();

  const toggleStep = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const copyText = async (text, okKey) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success(t(okKey));
    } catch (_) {
      message.error(t("voiceCaptureManagement.copy_failed"));
    }
  };

  const renderStepBody = (key) => {
    if (key === "api" && !apiReady) {
      return (
        <pre className={styles.setupCode}>
          {t("voiceCaptureManagement.setup_step_api_cmd")}
        </pre>
      );
    }
    if (key === "active" && widget && !steps.active && onActivate) {
      return (
        <Button
          type="primary"
          size="small"
          loading={activating}
          onClick={onActivate}
          className={styles.checklistActionBtn}
        >
          {t("voiceCaptureManagement.activate_widget")}
        </Button>
      );
    }
    if (key === "web_embed" && widget && !steps.web_embed && onEnableEmbed) {
      return (
        <Button
          type="primary"
          size="small"
          loading={enablingEmbed}
          onClick={onEnableEmbed}
          className={styles.checklistActionBtn}
        >
          {t("voiceCaptureManagement.enable_embed")}
        </Button>
      );
    }
    if (key === "snippet" && embedSnippet) {
      return (
        <div className={styles.checklistCodeBlock}>
          <div className={styles.checklistCodeHeader}>
            <span>{t("voiceCaptureManagement.embed_snippet")}</span>
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={() =>
                copyText(embedSnippet, "voiceCaptureManagement.copied_embed")
              }
            >
              {t("voiceCaptureManagement.copy")}
            </Button>
          </div>
          <pre className={styles.setupCode}>{embedSnippet}</pre>
        </div>
      );
    }
    if (key === "cdn") {
      return (
        <div className={styles.checklistCodeBlock}>
          <div className={styles.checklistCodeHeader}>
            <span>{t("voiceCaptureManagement.setup_step_cdn_title")}</span>
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={() =>
                copyText(cdnUrl, "voiceCaptureManagement.copied_cdn")
              }
            >
              {t("voiceCaptureManagement.copy")}
            </Button>
          </div>
          <pre className={styles.setupCode}>{cdnUrl}</pre>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={styles.checklist}>
      {showProgressHeader ? (
        <div className={styles.checklistProgressHeader}>
          <span className={styles.checklistProgressLabel}>
            {t("voiceCaptureManagement.steps_complete", {
              done: progress.done,
              total: progress.total,
            })}
          </span>
          <div className={styles.checklistProgressTrack}>
            <div
              className={styles.checklistProgressFill}
              style={{
                width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%`,
              }}
            />
          </div>
        </div>
      ) : null}

      <ol className={styles.checklistList}>
        {SETUP_STEP_KEYS.map((key) => {
          const done = steps[key] === true;
          const isOpen = expanded.has(key);
          const body = renderStepBody(key);
          const canExpand = !!body || !done;

          return (
            <li
              key={key}
              className={[
                styles.checklistItem,
                done ? styles.checklistItemDone : "",
              ].join(" ")}
            >
              <button
                type="button"
                className={styles.checklistItemHead}
                onClick={() => canExpand && toggleStep(key)}
                disabled={!canExpand}
              >
                <span className={styles.checklistIndicator}>
                  {done ? (
                    <CheckCircleFilled className={styles.checklistIconDone} />
                  ) : (
                    <span className={styles.checklistCircle} />
                  )}
                </span>
                <span
                  className={[
                    styles.checklistItemTitle,
                    done ? styles.checklistItemTitleDone : "",
                  ].join(" ")}
                >
                  {t(`voiceCaptureManagement.setup_step_${key}_title`)}
                </span>
                {canExpand ? (
                  isOpen ? (
                    <DownOutlined className={styles.checklistChevron} />
                  ) : (
                    <RightOutlined className={styles.checklistChevron} />
                  )
                ) : null}
              </button>
              {!done ? (
                <p className={styles.checklistItemDesc}>
                  {t(`voiceCaptureManagement.setup_step_${key}_desc`)}
                </p>
              ) : (
                <p className={styles.checklistItemDescDone}>
                  {t(`voiceCaptureManagement.setup_step_${key}_desc`)}
                </p>
              )}
              {isOpen && body ? (
                <div className={styles.checklistItemBody}>{body}</div>
              ) : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default VoiceCaptureSetupChecklist;
